// src/api/notes.ts — Notemaker API client (Task Management → Notemaker).
//
// Notes belong to the signed-in user; the backend (hrms routers/notes.py)
// sanitizes the editor HTML on write and mirrors it to plain text for
// search/snippets. A note can be shared with colleagues: they see it under
// "All" and "Shared with me" and may edit its title and body (pin, archive,
// colour, tags, share and delete stay with the owner). Every edit is logged
// — `history()` says who changed what. The list call also returns the counts
// and tag cloud so the sidebar refreshes with a single request.
import api from "./api";

export type NoteScope = "all" | "pinned" | "archived" | "trash" | "shared";

export type NoteColor =
  | "default"
  | "sand"
  | "mint"
  | "sky"
  | "lilac"
  | "rose"
  | "lemon"
  | "peach";

export interface NoteShareUser {
  user_id: string;
  name: string;
  email: string | null;
}

export interface NoteSummary {
  id: string;
  title: string;
  snippet: string;        // first ~180 chars of the plain-text body
  color: NoteColor;
  tags: string[];
  is_pinned: boolean;
  is_archived: boolean;
  deleted_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  word_count: number;
  char_count: number;
  owner_id: string;
  // Set when the note is someone else's, shared with me (I can edit the
  // title/body, not its pin/archive/colour/tags).
  owner_name: string | null;
  is_shared: boolean;
  // Owner side: who it is shared with. Empty for a note shared with me.
  shared_with: NoteShareUser[];
  // Who made the last title/body edit (null on notes not edited since
  // edit tracking began).
  last_edited_by: string | null;
  last_edited_by_name: string | null;
}

export interface NoteEdit {
  id: string;
  user_id: string | null;
  user_name: string;
  is_me: boolean;
  fields: string[];
  // One line per change: 'Title: "a" → "b"', 'Added: "…"', 'Removed: "…"'.
  summary: string[];
  started_at: string | null;
  edited_at: string | null;
}

export interface Note extends NoteSummary {
  content: string;        // sanitized HTML
}

export interface NoteTag {
  name: string;
  count: number;
}

export interface NoteCounts {
  all: number;
  pinned: number;
  archived: number;
  trash: number;
  shared: number;
}

export interface NoteListResponse {
  notes: NoteSummary[];
  counts: NoteCounts;
  tags: NoteTag[];
}

export interface NoteListParams {
  scope?: NoteScope;
  q?: string;
  tag?: string;
  color?: NoteColor;
}

export interface NoteCreate {
  title?: string;
  content?: string;
  color?: NoteColor;
  tags?: string[];
  is_pinned?: boolean;
}

export interface NotePatch {
  title?: string;
  content?: string;
  color?: NoteColor;
  tags?: string[];
  is_pinned?: boolean;
  is_archived?: boolean;
}

export type NoteBulkAction = "archive" | "unarchive" | "trash" | "restore" | "pin" | "unpin";

export const notesApi = {
  list: (params: NoteListParams = {}) =>
    api.get<NoteListResponse>("/notes", {
      params: {
        scope: params.scope || "all",
        q: params.q || undefined,
        tag: params.tag || undefined,
        color: params.color || undefined,
      },
    }),
  get: (id: string) => api.get<Note>(`/notes/${encodeURIComponent(id)}`),
  create: (payload: NoteCreate) => api.post<Note>("/notes", payload),
  update: (id: string, payload: NotePatch) =>
    api.patch<Note>(`/notes/${encodeURIComponent(id)}`, payload),
  duplicate: (id: string) => api.post<Note>(`/notes/${encodeURIComponent(id)}/duplicate`),
  // Edit log, newest first (owner and recipients alike).
  history: (id: string) =>
    api.get<{ items: NoteEdit[] }>(`/notes/${encodeURIComponent(id)}/history`),
  // Soft delete → Trash. `permanent` removes the row for good.
  remove: (id: string, permanent = false) =>
    api.delete<{ ok: boolean; permanent: boolean; id: string }>(
      `/notes/${encodeURIComponent(id)}`,
      { params: permanent ? { permanent: true } : undefined }
    ),
  restore: (id: string) => api.post<Note>(`/notes/${encodeURIComponent(id)}/restore`),
  emptyTrash: () => api.post<{ ok: boolean; removed: number }>("/notes/trash/empty"),
  bulk: (ids: string[], action: NoteBulkAction) =>
    api.post<{ ok: boolean; updated: number }>("/notes/bulk", { ids, action }),
  // Replaces the recipient set: send everyone who should keep access.
  share: (id: string, user_ids: string[]) =>
    api.post<{ ok: boolean; shared_with: NoteShareUser[]; note: Note }>(
      `/notes/${encodeURIComponent(id)}/share`,
      { user_ids }
    ),
};

// "X shared a note with you" alerts ride the shared notification bell via a
// namespaced task_id "note:<note_id>"; the bell hydrates this feed and marks
// rows read on dismissal (mirrors the asset/expense feeds).
export const getNoteNotifications = (limit = 50) =>
  api.get(`/notes/notifications/unread?limit=${limit}`);

export const markNoteNotificationRead = (id: string) =>
  api.post(`/notes/notifications/${encodeURIComponent(id)}/read`);

export default notesApi;
